import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["choice", "status"]
  static values = { url: String, formId: String }

  connect() {
    this.persistedIntent = this.selectedIntent()
    this.latestIntent = this.persistedIntent
    this.saveQueue = Promise.resolve()
    this.setState("idle")

    this.form = this.formIdValue ? document.getElementById(this.formIdValue) : null
    this.boundSubmit = (event) => this.handleSubmit(event)
    this.boundBeforeVisit = (event) => this.handleBeforeVisit(event)
    this.form?.addEventListener("submit", this.boundSubmit)
    document.addEventListener("turbo:before-visit", this.boundBeforeVisit)
  }

  disconnect() {
    this.form?.removeEventListener("submit", this.boundSubmit)
    document.removeEventListener("turbo:before-visit", this.boundBeforeVisit)
  }

  choose(event) {
    const selected = event.currentTarget.value
    this.latestIntent = selected
    this.renderSelection(selected)
    this.showStatus("Saving…")
    this.setState("pending")

    // Serialize writes in click order. A slow earlier request can never land
    // after the user's final choice and overwrite it.
    this.saveQueue = this.saveQueue
      .catch(() => {})
      .then(() => this.persist(selected))
  }

  async persist(selected) {
    const body = new FormData()
    body.append("monitoring_intent", selected)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        body,
        keepalive: true,
      })
      if (!response.ok) throw new Error(`Monitoring intent save failed (${response.status})`)

      this.persistedIntent = selected
      if (this.latestIntent === selected) {
        this.showStatus("")
        this.setState("idle")
        this.resumeDeferredNavigation()
        this.resumeDeferredSubmit()
      }
    } catch (_error) {
      if (this.latestIntent === selected) {
        this.latestIntent = this.persistedIntent
        this.renderSelection(this.persistedIntent)
        this.showStatus("We couldn't save that choice. Please select it again.")
        this.setState("error")
        this.deferredSubmit = null
      }
    }
  }

  selectedIntent() {
    return this.choiceTargets.find((choice) => choice.checked)?.value || ""
  }

  renderSelection(selected) {
    this.choiceTargets.forEach((choice) => {
      choice.checked = choice.value === selected
    })
  }

  showStatus(message) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.classList.toggle("hidden", !message)
  }

  setState(state) {
    this.element.dataset.monitoringIntentState = state
    this.element.setAttribute("aria-busy", state === "pending" ? "true" : "false")
    this.element.dispatchEvent(new CustomEvent("monitoring-intent:state", {
      bubbles: true,
      detail: { state },
    }))
  }

  handleSubmit(event) {
    if (this.allowSubmit) return

    if (!this.selectedIntent()) {
      event.preventDefault()
      this.showRequiredError()
      return
    }

    if (this.element.dataset.monitoringIntentState !== "pending") return

    event.preventDefault()
    this.deferredSubmit = { form: event.currentTarget, submitter: event.submitter }
  }

  handleInvalid(event) {
    if (this.selectedIntent()) return

    event.preventDefault()
    this.showRequiredError()
  }

  showRequiredError() {
    this.showStatus("Choose what you're monitoring to continue.")
    this.choiceTargets[0]?.focus()
  }

  resumeDeferredSubmit() {
    if (!this.deferredSubmit) return

    const { form, submitter } = this.deferredSubmit
    this.deferredSubmit = null
    this.allowSubmit = true
    if (submitter) {
      form.requestSubmit(submitter)
    } else {
      form.requestSubmit()
    }
  }

  // Turbo refreshes can advance the loading screen while the answer is still
  // saving. Hold that visit until the serialized queue commits the final choice.
  handleBeforeVisit(event) {
    if (this.element.dataset.monitoringIntentState !== "pending") return

    event.preventDefault()
    this.deferredVisitUrl = event.detail.url
  }

  resumeDeferredNavigation() {
    if (!this.deferredVisitUrl) return

    const url = this.deferredVisitUrl
    this.deferredVisitUrl = null
    window.location.assign(url)
  }
}
